import { Badge, Stack, Typography, badgeClasses, styled } from "@mui/material"
import { formatNumber } from "../../dataDisplay"
import type { CounterVariants, SideNavLinkProps } from "./SideNavLink"

type SideNavCounter = Pick<
  SideNavLinkProps,
  "counter" | "counterTotal" | "counterVariant" | "counterMax"
> & {
  isActive: boolean
}
export const SideNavCounter = ({
  counter,
  counterTotal,
  counterVariant = "new",
  counterMax,
  isActive,
}: SideNavCounter) => {
  if (counter === null || counter === undefined) {
    return null
  }

  let badgeContent = counter

  if (typeof counter === "number") {
    let currentCounter = formatNumber(counter || 0)

    if (counterMax !== undefined && counter > counterMax) {
      currentCounter = `${formatNumber(counterMax)}+`
    }

    badgeContent = (
      <Stack direction="row" spacing={0.5}>
        <Typography
          fontSize="inherit"
          color={counterTotal !== undefined ? "common.electricBlue" : "inherit"}
        >
          {currentCounter}
        </Typography>
        {counterTotal !== undefined && (
          <>
            <Typography fontSize="inherit">/</Typography>
            <Typography fontSize="inherit">{counterTotal}</Typography>
          </>
        )}
      </Stack>
    )
  }

  return (
    <Counter
      badgeContent={badgeContent}
      counterVariant={counterVariant}
      active={isActive}
      sx={{ ml: 1 }}
    />
  )
}

export interface CounterProps {
  counterVariant: CounterVariants
  active: boolean
}

const Counter = styled(Badge, {
  shouldForwardProp: (prop) => !["counterVariant", "active"].includes(String(prop)),
})<CounterProps>(({ active, counterVariant, theme }) => {
  const badgeStyle = {
    whiteSpace: "nowrap",
    position: "initial",
    fontSize: theme.typography.pxToRem(12),
    transform: "none",
  } as const
  const color = active ? theme.palette.primary.main : theme.palette.grey.dark
  switch (counterVariant) {
    case "count":
      return {
        [`.${badgeClasses.badge}`]: {
          ...badgeStyle,
          color,
          backgroundColor: "white",
          border: `1px solid ${color}`,
        },
      }
    case "warn":
      return {
        [`.${badgeClasses.badge}`]: {
          ...badgeStyle,
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.common.white,
        },
      }
    default:
      return { [`.${badgeClasses.badge}`]: badgeStyle }
  }
})
Counter.defaultProps = {
  color: "primary",
}
